import { useEffect, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../lib/firebase';

export type FileUrlState = { url: string | null; status: 'idle' | 'loading' | 'ready' | 'error' };

/**
 * Resolve a storage path (or pass through a direct https url) to a download URL.
 * A missing/unreachable bucket resolves to status 'error' so callers can show a
 * graceful fallback instead of crashing.
 */
export function useFileUrl(storagePath?: string, directUrl?: string): FileUrlState {
  const [state, setState] = useState<FileUrlState>({ url: directUrl ?? null, status: directUrl ? 'ready' : 'idle' });

  useEffect(() => {
    if (!storagePath) {
      setState({ url: directUrl ?? null, status: directUrl ? 'ready' : 'idle' });
      return;
    }
    let alive = true;
    setState(s => ({ ...s, url: null, status: 'loading' }));
    getDownloadURL(ref(storage, storagePath))
      .then(u => {
        if (alive) setState({ url: u, status: 'ready' });
      })
      .catch(() => {
        if (alive) setState({ url: directUrl ?? null, status: directUrl ? 'ready' : 'error' });
      });
    return () => {
      alive = false;
    };
  }, [storagePath, directUrl]);

  return state;
}
